// T-171 (design D14): a rows field of a form definition — the table of rows, the «new row» part with its add button, what the
// rows post. A new row is added as typed, or checked by the definition's AddVia action, which answers a patch (row-add / rows /
// error). The rows' business (a share, a duplicate) is the action's; this keeps the table and the posted values.
import { $, $$, esc, icon } from './core.ts';
import { cellText, MESSAGES, rowsFrom, rowsPost, rowsProblem, type Row, type RowsConfig } from './form-logic.ts';
import type { EnhancedSelect } from './select.ts';

type Control = HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;

/** What the form gives a rows field: its message line and the server call for a checked add. */
export interface RowsHost {
  error: (key: string, message: string) => void;
  /** Posts the form with extra values to url and applies the answer's patch; resolves true when the patch had no error. */
  ask: (url: string, extra: [string, string][]) => Promise<boolean>;
  changed: (key: string) => void;
}

/** A mounted rows field. */
export interface RowsHandle {
  readonly key: string;
  readonly config: RowsConfig;
  get: () => Row[];
  set: (rows: Row[]) => void;
  add: (row: Row) => void;
  remove: (index: number) => void;
  /** The message for too few / too many rows, or null. */
  problem: () => string | null;
}

const MIN = 'دست‌کم یک ردیف اضافه کنید.';
const MAX = 'تعداد ردیف‌ها بیشتر از حد مجاز است.';
const REQUIRED = MESSAGES.required;

export function mountRows(
  frame: HTMLElement,
  key: string,
  config: RowsConfig,
  name: string,
  host: RowsHost,
): RowsHandle {
  const store = $<HTMLInputElement>(`[data-ds-rows-store="${CSS.escape(key)}"]`, frame)!;
  const indexed = $(`[data-ds-rows-indexed="${CSS.escape(key)}"]`, frame)!;
  const body = $<HTMLTableSectionElement>('.ds-rows-table tbody', frame)!;
  const shown = config.fields.filter(f => f.kind !== 'hidden');
  let rows: Row[] = [];
  try {
    rows = rowsFrom(JSON.parse(store.value || '[]'));
  } catch {
    rows = [];
  }

  const sync = (): void => {
    const post = rowsPost(name, config, rows);
    if (config.post === 'json') store.value = post[0][1];
    else {
      store.value = JSON.stringify(rows.map(r => r.values));
      indexed.innerHTML = post.map(([n, v]) => `<input type="hidden" name="${esc(n)}" value="${esc(v)}">`).join('');
    }
    // design D16: a unique column's values are left out of its new-row control's choices
    for (const f of config.fields.filter(c => c.unique)) {
      const el = draftControl(f.draft);
      if (el) el.dataset.exclude = JSON.stringify(rows.map(r => r.values[f.key]).filter(v => v != null && v !== ''));
    }
  };

  const draw = (): void => {
    body.innerHTML = rows
      .map(
        (r, i) =>
          `<tr data-ds-row="${i}"><td class="ds-rows-cell-num">${i + 1}</td>` +
          shown.map(f => `<td data-cell="${esc(f.key)}">${esc(cellText(r, f.key, f.kind))}</td>`).join('') +
          (config.removable
            ? `<td class="text-end"><button type="button" class="btn btn-sm btn-icon btn-soft" data-ds-rows-remove="${i}" aria-label="حذف ردیف ${i + 1}">${icon('trash')}</button></td>`
            : '') +
          '</tr>',
      )
      .join('');
    sync();
  };

  const draftControl = (id: string): Control | null => document.getElementById(id) as Control | null;

  const draftRow = (): { row: Row; missing: string[] } => {
    const values: Record<string, string | null> = {};
    const texts: Record<string, string | null> = {};
    const missing: string[] = [];
    for (const f of config.fields.filter(c => c.kind !== 'hidden' && !c.shown)) {
      const el = draftControl(f.draft);
      if (!el) continue;
      const value = el instanceof HTMLInputElement && el.type === 'checkbox' ? String(el.checked) : el.value.trim();
      values[f.key] = value;
      if (el instanceof HTMLSelectElement && value !== '')
        texts[f.key] = [...el.selectedOptions].at(0)?.textContent.trim() ?? value;
      if (f.required && value === '') missing.push(f.draft);
    }
    return { row: { values, texts }, missing };
  };

  const clearDraft = (): void => {
    for (const f of config.fields.filter(c => c.kind !== 'hidden' && !c.shown)) {
      const el = draftControl(f.draft);
      if (!el) continue;
      if (el instanceof HTMLInputElement && el.type === 'checkbox') el.checked = false;
      else el.value = '';
      (el as EnhancedSelect)._ds?.refresh();
      draftMessage(el, null);
    }
  };

  const draftMessage = (el: Control, text: string | null): void => {
    const field = el.closest<HTMLElement>('.ds-field');
    if (!field) return;
    field.classList.toggle('is-invalid', text !== null);
    const msg = field.querySelector<HTMLElement>(':scope > .ds-msg') ?? field.querySelector<HTMLElement>('.ds-msg');
    if (!msg) return;
    if (msg.dataset.info === undefined) msg.dataset.info = msg.innerHTML;
    if (text === null) msg.innerHTML = msg.dataset.info;
    else msg.textContent = text;
  };

  const handle: RowsHandle = {
    key,
    config,
    get: () => rows.map(r => ({ values: { ...r.values }, texts: r.texts ? { ...r.texts } : null })),
    set(next) {
      rows = next;
      draw();
      host.error(key, '');
      host.changed(key);
    },
    add(row) {
      rows = [...rows, row];
      draw();
      host.error(key, '');
      host.changed(key);
    },
    remove(index) {
      rows = rows.filter((_, i) => i !== index);
      draw();
      host.changed(key);
    },
    problem() {
      const p = rowsProblem(config, rows.length);
      return p === 'min' ? (config.minMessage ?? MIN) : p === 'max' ? MAX : null;
    },
  };

  frame.addEventListener('click', e => {
    const target = e.target as Element;
    const remove = target.closest<HTMLElement>('[data-ds-rows-remove]');
    if (remove) {
      const index = Number(remove.dataset.dsRowsRemove);
      if (!config.remove) handle.remove(index);
      else {
        // design D16: the server removes (its own rule) and answers the rows left
        remove.setAttribute('disabled', '');
        void host
          .ask(config.remove, [
            ['_rows', key],
            ['_remove', String(index)],
          ])
          .finally(() => remove.removeAttribute('disabled'));
      }
      return;
    }
    const add = target.closest<HTMLButtonElement>('[data-ds-rows-add]');
    if (!add) return;
    const { row, missing } = draftRow();
    $$<Control>('.ds-rows-draft input, .ds-rows-draft select, .ds-rows-draft textarea', frame).forEach(el =>
      draftMessage(el, null),
    );
    if (missing.length) {
      missing.forEach(id => {
        const el = draftControl(id);
        if (el) draftMessage(el, REQUIRED);
      });
      return;
    }
    const repeated = config.fields.find(
      f => f.unique && row.values[f.key] && rows.some(r => r.values[f.key] === row.values[f.key]),
    );
    if (repeated) {
      const el = draftControl(repeated.draft);
      if (el) draftMessage(el, MESSAGES.duplicate);
      return;
    }
    if (config.max != null && rows.length >= config.max) {
      host.error(key, MAX);
      return;
    }
    if (!config.add) {
      handle.add(row);
      clearDraft();
      return;
    }
    add.disabled = true;
    const extra = Object.entries(row.values).map(([k, v]) => ['row.' + k, v ?? ''] as [string, string]);
    void host
      .ask(config.add, [['_rows', key], ...extra])
      .then(ok => {
        if (ok) clearDraft();
      })
      .finally(() => {
        add.disabled = false;
      });
  });

  draw();
  return handle;
}
